import { Box, CircularProgress, Collapse, FormHelperText, Grid } from '@material-ui/core'
import { useField, useFormikContext } from 'formik'
import React, { useCallback, useMemo, useState } from 'react'
import { EVENTS, FIELD_MODE } from '~/common/constants'
import { useFieldError } from '~/common/hooks'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { Button } from '~/components/button'
import { Typography } from '~/components/typography'
import {
	imageBoxStyle,
	inputStyle,
	itemBoxStyle,
	labelStyle,
	previewFileWrapper,
	uploadButtonStyle,
	uploadInputStyled,
	wrapperStyle,
} from './file-upload-field.style'
import { captureException, removeFile as defaultRemoveFile, uploadFile as defaultUploadFile } from '~/common/helpers'
import { filter, findIndex, startsWith } from 'lodash'
import { AddImageSvg, DocSvg, PdfSvg } from '~/components/icons'
import { useDebounce } from 'react-use'
import { eventBus } from 'mobx-event-bus2'
import UploadListImage from '~/components/fields/file-upload-field/upload-list-image'

import { ImagePreviewCarousel } from '~/components/image-preview-carousel'
import UploadListDoc from '~/components/fields/file-upload-field/upload-list-doc'
import UploadListPdf from '~/components/fields/file-upload-field/upload-list-pdf'
import { useUploadField } from './useUploadField'

const ICONS = {
	'application/doc': DocSvg,
	'application/msword': DocSvg,
	'application/ms-doc': DocSvg,
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': DocSvg,
	'application/pdf': PdfSvg,
	'application/xml': DocSvg,
}

const IMAGE_TYPE = 'image/'

export const FileUploadField = ({
	name,
	label,
	validate,
	mode,
	multiple,
	camera,
	uploadFile,
	removeFile,
	accept,
	title,
	subTitle,
	hideDelete = false,
	onRefreshFileUrl,
}) => {
	const { t } = useTranslation()
	const { setFieldValue } = useFormikContext()
	const [processingItems, setProcesingItems] = useState([])

	const validateFunc = useCallback(
		(value) => {
			if (processingItems?.length > 0) {
				return 'UPLOADING'
			}

			if (typeof validate === 'function') {
				return validate(value)
			}
		},
		[validate, processingItems]
	)

	const [field, meta] = useField({ name, validate: validateFunc })

	const id = useMemo(() => `${name}_file_upload`, [name])

	const error = useFieldError(meta)
	const { listImageSlider, handleSelectImgList, handlePreviewImage, handlePreviewPdf, activeImage, showPreview, setShowPreview } = useUploadField(field.value)

	useDebounce(
		async () => {
			if (processingItems.length === 0) {
				return
			}

			const [file, ...rest] = processingItems

			try {
				const res = await uploadFile(file)
				const { signedBlobId, preSignedUrl, blobId } = res

				const values = [
					...(field.value || []),
					{
						filename: file.name,
						contentType: file.type,
						signedBlobId,
						fileUrl: preSignedUrl,
						blobId: blobId,
						byteSize: file.size,
					},
				]
				field.onChange({ target: { name, value: values } })
			} catch (error) {
				captureException('File upload failed.', error)
				eventBus.post(EVENTS.notifyStore.fireError, { message: `File upload failed. "${file.name}"` })
			} finally {
				setProcesingItems(rest)
			}
		},
		300,
		[processingItems]
	)

	const handleChange = useCallback(async (event) => {
		const files = Array.from(event?.target?.files)
		event.target.value = ''

		const invalidFileSize = files?.some((file) => file.size >= 10 * 1000 * 1000)
		const invalidFileType = files?.some((file) => !(Object.keys(ICONS).includes(file?.type) || startsWith(file.type, IMAGE_TYPE)))

		if (invalidFileSize) {
			return eventBus.post(EVENTS.notifyStore.fireError, { message: '$ERRORS.MAXIMUM_FILE_SIZE' })
		}

		if (invalidFileType) {
			return eventBus.post(EVENTS.notifyStore.fireError, { message: '$ERRORS.UNSUPPORT_CONTENT_TYPE' })
		}

		setProcesingItems(files)
	}, [])

	const handleDelete = useCallback(
		async (fileUrl) => {
			const index = findIndex(field?.value, (file) => file.fileUrl === fileUrl)
			const file = field?.value?.[index]
			if (file.id) {
				setFieldValue(`${name}.${index}._destroy`, true)
				setTimeout(() => removeFile(file.id), 100)
			}
			setFieldValue(`${name}.${index}._destroy`, true)
		},
		[setFieldValue, name, removeFile, field.value]
	)

	const visible = useMemo(() => field?.value?.filter((item) => !item._destroy)?.length > 0 || processingItems.length > 0, [field.value, processingItems])

	const disabled = useMemo(() => processingItems?.length > 0, [processingItems])

	const actualViewValue = filter(field?.value, (v) => !v._destroy)

	const hideUpload = useMemo(() => !multiple && (processingItems?.length > 0 || actualViewValue.length > 0), [processingItems, multiple, actualViewValue])

	return (
		<Box>
			{label && <Typography css={labelStyle}>{t(label)}</Typography>}
			<Box className="field-preview" css={previewFileWrapper}>
				<Box>
					{(!actualViewValue || actualViewValue.length === 0) && label && mode === FIELD_MODE.view && (
						<Box mt={1} mb={1}>
							{t('$PLACEHOLDERS.NONE')}
						</Box>
					)}
					<Collapse in={visible}>
						<Grid container spacing={2}>
							{field.value
								?.filter((item) => !(item._destroy === true))
								?.map(
									(item, index) =>
										!item?._destroy &&
										(item?.contentType === 'application/pdf' ? (
											<UploadListPdf
												key={index}
												onClick={() => handlePreviewPdf(item)}
												item={item}
												mode={mode}
												onClick1={(e) => {
													e.stopPropagation()
													handleDelete(item.fileUrl)
												}}
												hideDelete={hideDelete && item.id}
												onRefreshFileUrl={onRefreshFileUrl}
											/>
										) : item?.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
										  item?.contentType === 'application/msword' ||
										  item?.contentType === 'application/doc' ||
										  item?.contentType === 'application/docx' ||
										  item?.contentType === 'application/x-ole-storage' ? (
											<UploadListDoc
												key={index}
												onClick={() => handlePreviewPdf(item)}
												item={item}
												mode={mode}
												onClick1={(e) => {
													e.stopPropagation()
													handleDelete(item.fileUrl)
												}}
												hideDelete={hideDelete && item.id}
												onRefreshFileUrl={onRefreshFileUrl}
											/>
										) : (
											<UploadListImage
												key={index}
												onClick={() => handlePreviewImage(item)}
												item={item}
												mode={mode}
												className="image"
												onClick1={(e) => {
													e.stopPropagation()
													handleDelete(item.fileUrl)
												}}
												hideDelete={hideDelete && item.id}
												onRefreshFileUrl={onRefreshFileUrl}
											/>
										))
								)}

							{processingItems?.map((item, index) => (
								<Grid item xs={12} sm={6} key={index}>
									<Box justifyContent="center" css={itemBoxStyle} key={`${item.name}_${index}`}>
										<CircularProgress size={18} />
									</Box>
								</Grid>
							))}
							<ImagePreviewCarousel
								className="image-preview-carousel"
								handlePreviewPdf={handlePreviewPdf}
								handleSelectImgList={handleSelectImgList}
								listImageSlider={listImageSlider}
								open={showPreview}
								activeImage={activeImage}
								onClose={() => setShowPreview(false)}
							/>
						</Grid>
					</Collapse>
				</Box>
				{mode === FIELD_MODE.edit && !hideUpload && (
					<Box className="field-input" css={uploadInputStyled}>
						<input
							disabled={disabled}
							accept={accept}
							max={3}
							multiple={multiple}
							camera={camera}
							id={id}
							type="file"
							css={inputStyle}
							onChange={handleChange}
						/>

						<label htmlFor={id} css={wrapperStyle}>
							<Button border={2} variant="outlined" color="primary" component="span" css={uploadButtonStyle}>
								<Box className="content"></Box>
							</Button>

							<Box pl={2} pr={2} css={imageBoxStyle}>
								<div className="container">
									<div className="icon">
										<AddImageSvg />
									</div>
									<Box className="upload-comp">
										<div className="upload-comp-title">{t(title)}</div>
										<div className="upload-comp-sub-title">{t(subTitle)}</div>
									</Box>
								</div>
							</Box>
						</label>
						{error && (
							<FormHelperText error={true} style={{ marginTop: 15 }}>
								{error}
							</FormHelperText>
						)}
					</Box>
				)}
			</Box>
		</Box>
	)
}

FileUploadField.defaultProps = {
	mode: FIELD_MODE.edit,
	camera: true,
	removeFile: defaultRemoveFile,
	uploadFile: defaultUploadFile,
	accept: '.png,.jpg,.jpeg,.pdf,.doc,.docx',
	multiple: true,
	title: 'SELECT_FILE_TO_UPLOAD',
}
